import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { FormGroup, FormGroupDirective } from '@angular/forms';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { ISharedData } from 'src/app/shared/state-management/state-interfaces';
import { CommonService } from 'src/app/shared/utils/common.service';
import { resetForm, Utility } from 'src/app/shared/utils/utility.service';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import * as XLSX from 'xlsx';
import { IUser } from '../../user-management-interfaces';
import { UserManagementService } from '../../user-management.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
})
export class UserDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly searchSubject$ = new Subject<string | undefined>();

  /**
   * all necessary input get values from parent
   */
  @Input() formGroupName!: string;
  @Input() dialogRef?: any; //close event of parent component
  @Input() stepper!: number;
  @Output() stepperChange = new EventEmitter<number>();
  @ViewChild('userDetails') userDetails!: ElementRef;

  currentUser!: IUser | undefined;
  @Output() currentUserEmitter = new EventEmitter<IUser | undefined>();

  @ViewChild('csvReader') csvReader: any;
  formGroup!: FormGroup;
  submited = false;
  siteId!: string;
  sharedData?: ISharedData;
  shareedDataSubscription?: Subscription;
  currentAlphabet: string | undefined;
  userList$!: Observable<IUser[] | undefined>;
  isUserSearch: boolean = false;
  searchKeyPress: boolean = false;
  selectedUserPanel: boolean = false;
  userSearch: string = '';
  loader: boolean = false;
  readonly LANG_CONST = LANG_CONST;
  flag = false;
  readonly CONSTANTS = CONSTANTS;
  deactivateCSV: boolean = false;

  @Input() skipStepUserType: boolean = false;
  @Output() skipStepUserTypeChange = new EventEmitter<boolean>();

  @Input() skipStepBuilding: boolean = false;
  @Output() skipStepBuildingChange = new EventEmitter<boolean>();
  constructor(
    public rootFormGroup: FormGroupDirective,
    public commonService: CommonService,
    private userManagementService: UserManagementService,
    private sharedDataService: SharedDataService,
    private cdf: ChangeDetectorRef,
    private localizationService: LocalizationService
  ) {}

  /**
   * @description this is onInit life cycle method
   */
  ngOnInit(): void {
    this.localizationService.setupLocalization();
    this.shareedDataSubscription = this.sharedDataService.currentData.subscribe(
      (sharedData) => {
        this.sharedData = sharedData;
        const currentSiteId = this.sharedData?.selectedSite?.siteId;
        if (currentSiteId && currentSiteId !== this.siteId) {
          this.siteId = String(currentSiteId);
        }
      }
    );

    this.formGroup = this.rootFormGroup.control.get(
      this.formGroupName
    ) as FormGroup;
    if (
      this.formGroup.controls.firstName.status ===
      CONSTANTS.DISABLED.toUpperCase()
    ) {
      this.deactivateCSV = true;
    }
  }

  /**
   * @description read CSV format and transform data into reactive form control
   * @param $event get file event
   */
  uploadCsv($event: any): void {
    let workBook: any = {};
    const reader = new FileReader();
    const file = $event.target.files[0];
    reader.onload = () => {
      const data = reader.result;
      workBook = XLSX.read(data, { type: 'binary' });
      const sheet = workBook.Sheets[workBook.SheetNames];
      const parsedData: any = XLSX.utils.sheet_to_json(sheet, { raw: true });

      this.updateForm(parsedData[0]?.FirstName, parsedData[0]?.LastName, '');
    };
    reader.readAsBinaryString(file);
  }

  /**
   * @description navigate to back screen
   * @returns void
   */
  navigateBack(): void {
    this.stepperChange.emit((this.stepper += -1));
  }

  /**
   * @description patch the reactive form control by the given string value
   * @param firstName control name
   * @param lastName control name
   */
  updateForm(firstName: string, lastName: string, existingUserId: string) {
    this.formGroup?.patchValue({
      firstName: firstName,
      lastName: lastName,
      existingUserId: existingUserId,
    });
  }

  /**
   * @description validate form if form valid hold value into main form and redirect to next step
   * @returns void
   */
  submit(): void {
    this.submited = true;
    if (this.formGroup.invalid) return;
    this.stepperChange.emit(
      (this.stepper += 1 + (this.skipStepUserType === true ? +1 : 0))
    );
  }
  ngAfterViewInit(): void {
    if (this.userDetails) {
      this.userDetails.nativeElement.focus();
    }
  }

  /**
   * @description check alphabet is coming from first time and return first character of it
   * @param name {string}
   * @param index {number}
   * @returns return boolean state
   */
  checkIfNew(name: string, index: number): boolean {
    index === 0 ? (this.currentAlphabet = '') : null;
    if (this.currentAlphabet === name?.charAt(0).toLowerCase()) return false;
    this.currentAlphabet = name?.charAt(0).toLowerCase();
    return true;
  }

  /**
   * @param data encode string
   * @returns return encode data
   */
  encode(data: string): string {
    return Utility.encodeToBase64(data);
  }

  /**
   * @description search user if character greater than 0
   * @returns void
   */
  searchUser(): void {
    if (this.userSearch.length > 0) {
      this.searchKeyPress = true;
      this.searchSubject$.next(this.userSearch);
    } else {
      this.searchKeyPress = false;
      this.userList$ = of(undefined);
    }
  }

  /**
   * @description get selected user object
   * @param {user} user object
   * @returns void
   */
  selectUser(user: IUser): void {
    this.skipStepUserTypeChange.emit((this.skipStepUserType = true));
    if (
      user.userType === CONSTANTS.PROPERTY_MANAGER_ADMIN ||
      user.userType === CONSTANTS.PROPERTY_MANAGER_USER
    ) {
      this.skipStepBuildingChange.emit((this.skipStepBuilding = true));
    } else {
      this.skipStepBuildingChange.emit((this.skipStepBuilding = false));
    }
    // update when api integrate
    this.currentUser = {
      userId: user.userId,
      userType: user.userType, //user.userType,
      name: user.name,
      phoneNumber: user.phoneNumber,
      email: user.email,
    };

    this.userSearch = '';
    this.searchKeyPress = true;
    this.selectedUserPanel = true;

    this.formGroup.controls.firstName.clearValidators();
    this.formGroup.controls.lastName.clearValidators();
    this.updateForm(user.name!, '', user.email!);

    this.currentUserEmitter.emit(this.currentUser);
  }

  /**
   * @description selected user remove and reset all variables
   * @returns void
   */
  removeUser(): void {
    this.skipStepUserTypeChange.emit((this.skipStepUserType = false));

    this.searchKeyPress = false;
    this.selectedUserPanel = false;
    this.userSearch = '';
    this.currentUser = {} as IUser as IUser;
    this.userList$ = of(undefined);

    this.formGroup.controls.firstName.setValidators(
      MFValidators.compose([
        MFValidators.required,
        MFValidators.alphaNumeric,
        MFValidators.whitespace,
        MFValidators.maxLength(32),
      ])
    );
    this.formGroup.controls.lastName.setValidators(
      MFValidators.compose([
        MFValidators.required,
        MFValidators.alphaNumeric,
        MFValidators.whitespace,
        MFValidators.maxLength(32),
      ])
    );
    this.cdf.detectChanges(); // for form reset validation
    resetForm(this.formGroup);
    this.formGroup.updateValueAndValidity();

    this.currentUserEmitter.emit(this.currentUser);
  }

  /**
   * @description angular hook all onces when component leave, and unsubcribe all subcribtions
   * @returns void
   */
  ngOnDestroy(): void {
    this.shareedDataSubscription?.unsubscribe();
  }
}
